// PurchaseOrderItems.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

// Context
import { useBase } from 'context/BaseContext';
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';

// Services
import { moveNodeToTrash, updateNode } from 'services/nodes/NodeWriteService';
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// Utilities
import { formatPrice } from 'utilities/Currency';
import { getOptionLabel, resolveEnumOptionsForField } from 'utilities/EnumOptions';
import { buildNodeFieldPatch } from 'utilities/NodeFieldData';
import { buildNodeSubtitlePatch } from 'utilities/NodeSubtitle';

// Local
import AddPurchaseOrderItemFromProductModal from 'nodetypes/purchaseorderitem/fromProduct/AddPurchaseOrderItemFromProductModal';
import {
  PURCHASE_ORDER_ITEM_TYPE_KEY,
} from 'nodetypes/purchaseorderitem/purchaseOrderItemKeys';
import {
  PURCHASE_ORDER_PAYMENT_TYPE_KEY,
} from 'nodetypes/purchaseorder/view/purchaseOrderPaymentFromPo';
import {
  augmentPurchaseOrderItemTotalPatch,
  computePurchaseOrderItemsRollups,
  getPurchaseOrderItemRowModel,
  readPurchaseOrderItemField,
} from 'nodetypes/purchaseorder/view/purchaseOrderItemsModel';
import {
  DEFAULT_PURCHASE_ORDER_ITEMS_DISPLAY_PREFS,
  readPurchaseOrderItemsDisplayPrefs,
  writePurchaseOrderItemsDisplayPrefs,
} from 'nodetypes/purchaseorder/view/purchaseOrderItemsDisplayPrefs';
import {
  InlineMoneyCell,
  InlineNumberCell,
  InlineSelectCell,
  InlineTextCell,
} from 'nodetypes/purchaseorder/view/PurchaseOrderItemsInlineCells';
import PurchaseOrderItemsSettingsModal from 'nodetypes/purchaseorder/view/PurchaseOrderItemsSettingsModal';
import usePurchaseOrderRelatedNodes from 'nodetypes/purchaseorder/view/usePurchaseOrderRelatedNodes';

// CSS
import './PurchaseOrderItems.css';

// Components
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import Confirm from 'components/confirm/Confirm';
import EnumPillSelect from 'components/enumpillselect/EnumPillSelect';
import IconButton from 'components/iconbutton/IconButton';

function cleanString(value) {
  return String(value || '').trim();
}

function formatMoneyCell(value) {
  if (value === null || value === undefined || value === '') {
    return <span className="purchase-order-items-muted">—</span>;
  }
  return formatPrice(value);
}

function getItemSchemaField(itemType, fieldName) {
  const fields = Array.isArray(itemType?.schema?.fields) ? itemType.schema.fields : [];
  return fields.find((field) => cleanString(field?.name) === fieldName) || null;
}

const ItemThumb = React.memo(({ itemKey, coverUrl }) => {
  const [src, setSrc] = useState(coverUrl || '');

  useEffect(() => {
    let cancelled = false;
    const raw = cleanString(coverUrl);
    if (!raw) {
      setSrc('');
      return undefined;
    }
    if (
      raw.startsWith('blob:')
      || raw.startsWith('data:')
      || raw.startsWith('http://')
      || raw.startsWith('https://')
      || raw.startsWith('/')
    ) {
      setSrc(raw);
      return undefined;
    }
    (async () => {
      try {
        const signed = await resolveImageUrl({ nodeKey: itemKey || null, url: raw });
        if (!cancelled) setSrc(signed || raw);
      } catch {
        if (!cancelled) setSrc(raw);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [coverUrl, itemKey]);

  return (
    <div className="purchase-order-items-thumb" aria-hidden="true">
      {src ? (
        <img src={src} alt="" draggable="false" loading="lazy" />
      ) : null}
    </div>
  );
});

ItemThumb.displayName = 'ItemThumb';

/**
 * PurchaseOrderItems
 *
 * Houzz-inspired items table for PURCHASE_ORDER (display + status edit + footer rollups).
 */
const PurchaseOrderItems = React.memo(({
  node,
  nodeType = null,
  readOnly = false,
  neighborNodes: neighborNodesProp = null,
  onRelatedNodeOpen = null,
}) => {
  const { activeBase } = useBase();
  const { user } = useUser();
  const { getType } = useNodeTypes();
  const language = useCurrentLanguage();

  const [addOpen, setAddOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [displayPrefs, setDisplayPrefs] = useState(DEFAULT_PURCHASE_ORDER_ITEMS_DISPLAY_PREFS);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [removeBusy, setRemoveBusy] = useState(false);

  const baseKey = cleanString(activeBase?.key || node?.baseKey);
  const purchaseOrderKey = cleanString(node?.key);
  const canEdit = Boolean(!readOnly);
  const canOpenItem = typeof onRelatedNodeOpen === 'function';
  const showCoverPhoto = displayPrefs.showCoverPhoto !== false;
  const showDescription = displayPrefs.showDescription !== false;
  const showLineNumber = displayPrefs.showLineNumber !== false;
  const subscribedNeighbors = usePurchaseOrderRelatedNodes(
    neighborNodesProp == null ? purchaseOrderKey : null,
    neighborNodesProp == null ? baseKey : null,
  );
  const neighborNodes = neighborNodesProp == null ? subscribedNeighbors : neighborNodesProp;

  const itemType = useMemo(() => getType(PURCHASE_ORDER_ITEM_TYPE_KEY), [getType]);
  const statusField = useMemo(
    () => getItemSchemaField(itemType, 'status'),
    [itemType],
  );
  const unitField = useMemo(
    () => getItemSchemaField(itemType, 'unit'),
    [itemType],
  );
  const unitOptions = useMemo(() => {
    if (!unitField) return [];
    return resolveEnumOptionsForField(unitField, language).map((option) => ({
      value: String(option?.value ?? ''),
      label: getOptionLabel(option, language) || String(option?.value ?? ''),
    })).filter((option) => option.value);
  }, [language, unitField]);

  // Load display prefs when the base scope changes.
  useEffect(() => {
    setDisplayPrefs(readPurchaseOrderItemsDisplayPrefs(baseKey));
  }, [baseKey]);

  const items = useMemo(() => (
    (Array.isArray(neighborNodes) ? neighborNodes : [])
      .filter((row) => cleanString(row?.type).toUpperCase() === PURCHASE_ORDER_ITEM_TYPE_KEY)
      .sort((left, right) => {
        const leftOrder = Number(readPurchaseOrderItemField(left, 'sortOrder'));
        const rightOrder = Number(readPurchaseOrderItemField(right, 'sortOrder'));
        if (Number.isFinite(leftOrder) && Number.isFinite(rightOrder) && leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return cleanString(left?.title).localeCompare(cleanString(right?.title));
      })
  ), [neighborNodes]);

  const payments = useMemo(() => (
    (Array.isArray(neighborNodes) ? neighborNodes : [])
      .filter((row) => cleanString(row?.type).toUpperCase() === PURCHASE_ORDER_PAYMENT_TYPE_KEY)
  ), [neighborNodes]);

  // Drop selections for rows that are no longer in the table.
  useEffect(() => {
    const validKeys = new Set(
      items.map((item) => cleanString(item?.key)).filter(Boolean),
    );
    setSelectedKeys((prev) => {
      let changed = false;
      const next = new Set();
      prev.forEach((key) => {
        if (validKeys.has(key)) next.add(key);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [items]);

  const rows = useMemo(
    () => items.map((item) => ({
      item,
      model: getPurchaseOrderItemRowModel(item, itemType, language),
    })),
    [itemType, items, language],
  );

  const selectedCount = selectedKeys.size;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  const someSelected = selectedCount > 0;

  const rollups = useMemo(
    () => computePurchaseOrderItemsRollups(items, node, nodeType, payments),
    [items, node, nodeType, payments],
  );

  const handleItemChange = useCallback((itemKey, patch) => {
    if (!canEdit || !itemKey || !patch) return undefined;
    return updateNode(itemKey, patch);
  }, [canEdit]);

  /**
   * Commits an inline cell edit for a PO item field.
   *
   * @param {object} item
   * @param {string} fieldName
   * @param {unknown} nextValue
   * @param {string} [fieldType]
   * @returns {Promise|undefined}
   */
  const handleInlineCommit = useCallback((item, fieldName, nextValue, fieldType = 'string') => {
    if (!canEdit || !item?.key || !fieldName) return undefined;

    if (fieldName === 'title') {
      return updateNode(item.key, { title: cleanString(nextValue) });
    }

    if (fieldName === 'description') {
      return updateNode(item.key, buildNodeSubtitlePatch(nextValue));
    }

    const schemaField = getItemSchemaField(itemType, fieldName);
    const field = schemaField || {
      name: fieldName,
      source: 'purchase_order_item',
      type: fieldType,
    };

    let patch = buildNodeFieldPatch(item, field, PURCHASE_ORDER_ITEM_TYPE_KEY, nextValue);
    if (!patch) return undefined;

    patch = augmentPurchaseOrderItemTotalPatch({
      node: item,
      nodeTypeKey: PURCHASE_ORDER_ITEM_TYPE_KEY,
      field,
      value: nextValue,
      patch,
    }) || patch;

    return updateNode(item.key, patch);
  }, [canEdit, itemType]);

  const handleRowOpen = useCallback((item) => {
    if (!canOpenItem || !item?.key) return;
    onRelatedNodeOpen(item);
  }, [canOpenItem, onRelatedNodeOpen]);

  const handleDisplayPrefsSave = useCallback((nextPrefs) => {
    const saved = writePurchaseOrderItemsDisplayPrefs(baseKey, nextPrefs);
    setDisplayPrefs(saved);
  }, [baseKey]);

  /**
   * Toggles one row in the selection set.
   *
   * @param {string} itemKey
   * @returns {void}
   */
  const handleToggleRowSelected = useCallback((itemKey) => {
    const key = cleanString(itemKey);
    if (!key) return;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  /**
   * Selects all rows, or clears selection when all are already selected.
   *
   * @returns {void}
   */
  const handleToggleSelectAll = useCallback(() => {
    setSelectedKeys((prev) => {
      if (rows.length > 0 && prev.size === rows.length) {
        return new Set();
      }
      return new Set(
        rows.map(({ model }) => cleanString(model.key)).filter(Boolean),
      );
    });
  }, [rows]);

  /**
   * Moves selected line items to Trash.
   *
   * @returns {Promise<void>}
   */
  const handleRemoveSelectedConfirm = useCallback(async () => {
    if (!canEdit || removeBusy || selectedKeys.size === 0) return;

    const keys = [...selectedKeys];
    setRemoveBusy(true);
    try {
      await Promise.all(
        keys.map((key) => moveNodeToTrash(key, {
          trashedByKey: user?.key || null,
        })),
      );
      setSelectedKeys(new Set());
      setRemoveConfirmOpen(false);
    } catch (error) {
      toast.error(error?.message || 'Could not remove the selected items.', {
        autoClose: 4000,
      });
    } finally {
      setRemoveBusy(false);
    }
  }, [canEdit, removeBusy, selectedKeys, user?.key]);

  if (!node) return null;

  const tableClassName = [
    'purchase-order-items-table',
    showLineNumber ? '' : 'is-line-hidden',
    canEdit ? 'has-row-select' : '',
    canOpenItem ? 'has-row-actions' : '',
  ].filter(Boolean).join(' ');

  const removeConfirmMessage = selectedCount === 1
    ? 'Move this line item to Trash?'
    : `Move ${selectedCount} line items to Trash?`;

  return (
    <>
      <div className="purchase-order-items">
        <div className="purchase-order-items-header">
          <div className="purchase-order-items-title">
            {`Items (${rows.length})`}
          </div>

          {/* ACTIONS */}
          <div className="purchase-order-items-header-actions">
            <IconButton
              iconKey="discover_tune"
              title="Customize items"
              size={30}
              iconSize={18}
              onClick={() => setSettingsOpen(true)}
            />
            {canEdit && someSelected ? (
              <Button
                label="Remove Items"
                iconKey="delete"
                height="30px"
                enabled={!removeBusy}
                onClick={() => setRemoveConfirmOpen(true)}
              />
            ) : null}
            {canEdit ? (
              <Button
                label="Add Items"
                iconKey="add"
                height="30px"
                onClick={() => setAddOpen(true)}
              />
            ) : null}
          </div>
        </div>

        <div className="purchase-order-items-body">
          {rows.length === 0 ? (
            <div className="purchase-order-items-empty">
              No line items yet.
              {canEdit ? ' Add products from the library.' : ''}
            </div>
          ) : (
            <div className="purchase-order-items-scroll">
              <table className={tableClassName}>
                <thead>
                  <tr>
                    {canEdit ? (
                      <th className="purchase-order-items-col-select" scope="col">
                        <Checkbox
                          value={allSelected}
                          size={18}
                          onChange={handleToggleSelectAll}
                        />
                      </th>
                    ) : null}
                    {showLineNumber ? (
                      <th className="purchase-order-items-col-line" scope="col">#</th>
                    ) : null}
                    <th className="purchase-order-items-col-item">Item</th>
                    <th className="purchase-order-items-col-sku">SKU</th>
                    <th className="purchase-order-items-col-status">Status</th>
                    <th className="purchase-order-items-col-manufacturer">Manufacturer</th>
                    <th className="purchase-order-items-col-qty">Qty</th>
                    <th className="purchase-order-items-col-unit">Unit</th>
                    <th className="purchase-order-items-col-unit-price">Unit Price</th>
                    <th className="purchase-order-items-col-total">Total</th>
                    {canOpenItem ? (
                      <th className="purchase-order-items-col-actions" scope="col">
                        <span className="purchase-order-items-sr-only">Open</span>
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ item, model }, index) => (
                    <tr key={model.key}>
                      {canEdit ? (
                        <td className="purchase-order-items-col-select">
                          <Checkbox
                            value={selectedKeys.has(model.key)}
                            size={18}
                            onChange={() => handleToggleRowSelected(model.key)}
                          />
                        </td>
                      ) : null}
                      {showLineNumber ? (
                        <td className="purchase-order-items-col-line">
                          {index + 1}
                        </td>
                      ) : null}
                      <td className="purchase-order-items-col-item">
                        <div className="purchase-order-items-item-cell">
                          {showCoverPhoto ? (
                            <ItemThumb itemKey={model.key} coverUrl={model.coverUrl} />
                          ) : null}
                          <div className="purchase-order-items-item-text">
                            <InlineTextCell
                              value={model.title}
                              placeholder="Untitled"
                              canEdit={canEdit}
                              emphasized
                              onCommit={(nextValue) => (
                                handleInlineCommit(item, 'title', nextValue)
                              )}
                            />
                            {showDescription ? (
                              <InlineTextCell
                                value={canEdit ? model.description : model.subtitle}
                                placeholder={model.leadTime || 'Add description'}
                                canEdit={canEdit}
                                maxWidth={300}
                                onCommit={(nextValue) => (
                                  handleInlineCommit(item, 'description', nextValue)
                                )}
                              />
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="purchase-order-items-col-sku">
                        <InlineTextCell
                          value={model.sku}
                          placeholder="SKU"
                          canEdit={canEdit}
                          onCommit={(nextValue) => (
                            handleInlineCommit(item, 'sku', nextValue, 'string')
                          )}
                        />
                      </td>
                      <td className="purchase-order-items-col-status">
                        <div className="purchase-order-items-status">
                          {statusField ? (
                            <EnumPillSelect
                              field={statusField}
                              value={model.status}
                              node={item}
                              typeKey={PURCHASE_ORDER_ITEM_TYPE_KEY}
                              canEdit={canEdit}
                              onNodeChange={handleItemChange}
                              placeholder="Status…"
                              language={language}
                              fontSize="12px"
                            />
                          ) : (
                            model.status || (
                              <span className="purchase-order-items-muted">—</span>
                            )
                          )}
                        </div>
                      </td>
                      <td className="purchase-order-items-col-manufacturer">
                        <InlineTextCell
                          value={model.manufacturer}
                          placeholder="Manufacturer"
                          canEdit={canEdit}
                          onCommit={(nextValue) => (
                            handleInlineCommit(item, 'manufacturer', nextValue, 'string')
                          )}
                        />
                      </td>
                      <td className="purchase-order-items-col-qty">
                        <InlineNumberCell
                          value={model.quantity}
                          placeholder="1"
                          canEdit={canEdit}
                          onCommit={(nextValue) => (
                            handleInlineCommit(item, 'quantity', nextValue, 'number')
                          )}
                        />
                      </td>
                      <td className="purchase-order-items-col-unit">
                        <InlineSelectCell
                          value={model.unit}
                          options={unitOptions}
                          placeholder="Unit"
                          canEdit={canEdit && unitOptions.length > 0}
                          onCommit={(nextValue) => (
                            handleInlineCommit(item, 'unit', nextValue, 'enum')
                          )}
                        />
                      </td>
                      <td className="purchase-order-items-col-unit-price">
                        <InlineMoneyCell
                          valueCents={model.unitPrice}
                          canEdit={canEdit}
                          onCommit={(nextValue) => (
                            handleInlineCommit(item, 'unitPrice', nextValue, 'money')
                          )}
                        />
                      </td>
                      <td className="purchase-order-items-col-total">
                        {formatMoneyCell(model.total)}
                      </td>
                      {canOpenItem ? (
                        <td className="purchase-order-items-col-actions">
                          <div className="purchase-order-items-row-action">
                            <IconButton
                              iconKey="more_horiz"
                              title="Open item"
                              size={28}
                              iconSize={18}
                              onClick={() => handleRowOpen(item)}
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="purchase-order-items-footer">
            <div className="purchase-order-items-footer-row">
              <span className="purchase-order-items-footer-label">Subtotal</span>
              <span className="purchase-order-items-footer-value">
                {formatPrice(rollups.subTotal)}
              </span>
            </div>
            <div className="purchase-order-items-footer-row">
              <span className="purchase-order-items-footer-label">Tax</span>
              <span className="purchase-order-items-footer-value">
                {formatPrice(rollups.tax)}
              </span>
            </div>
            <hr className="purchase-order-items-footer-rule" />
            <div className="purchase-order-items-footer-row">
              <span className="purchase-order-items-footer-label">Total</span>
              <span className="purchase-order-items-footer-value">
                {formatPrice(rollups.total)}
              </span>
            </div>
            <div className="purchase-order-items-footer-row">
              <span className="purchase-order-items-footer-label">Amount Paid</span>
              <span className="purchase-order-items-footer-value">
                {formatPrice(rollups.amountPaid)}
              </span>
            </div>
            <hr className="purchase-order-items-footer-rule" />
            <div className="purchase-order-items-footer-row is-emphasis">
              <span className="purchase-order-items-footer-label">Balance Due</span>
              <span className="purchase-order-items-footer-value">
                {formatPrice(rollups.balanceDue)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <AddPurchaseOrderItemFromProductModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        purchaseOrderNode={node}
        baseKey={baseKey}
        user={user}
      />

      <PurchaseOrderItemsSettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        prefs={displayPrefs}
        onSave={handleDisplayPrefsSave}
      />

      <Confirm
        title="Remove Items"
        message={removeConfirmMessage}
        confirmLabel={removeBusy ? 'Removing…' : 'Move to Trash'}
        isOpen={removeConfirmOpen}
        setOpen={setRemoveConfirmOpen}
        onConfirm={handleRemoveSelectedConfirm}
      />
    </>
  );
});

PurchaseOrderItems.displayName = 'PurchaseOrderItems';

export default PurchaseOrderItems;
